import React, { useState, useEffect } from 'react';
import { 
  X, 
  ChevronRight, 
  ChevronLeft, 
  FileText, 
  Users, 
  CreditCard, 
  Ticket, 
  CheckCircle,
  Shield,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';

const TUTORIAL_STEPS = [
  {
    id: 'welcome',
    icon: Sparkles,
    titleFr: 'Bienvenue sur Middleman!',
    titleEn: 'Welcome to Middleman!',
    descFr: 'Découvrez comment sécuriser vos transferts de billets en quelques étapes simples.',
    descEn: 'Discover how to secure your ticket transfers in a few simple steps.',
    tipFr: 'Ce tutoriel ne prend que 1 minute',
    tipEn: 'This tutorial takes only 1 minute',
    color: 'from-brand-500 to-cyan-500'
  },
  {
    id: 'create',
    icon: FileText,
    titleFr: '1. Créer une demande de transfert',
    titleEn: '1. Create a transfer request',
    descFr: 'Que vous soyez acheteur ou vendeur, commencez par créer une demande de transfert en remplissant les informations sur l\'événement et les billets.',
    descEn: 'Whether you\'re a buyer or seller, start by creating a transfer request by filling in event and ticket information.',
    tipFr: 'Astuce: Utilisez votre numéro client pour identifier rapidement l\'autre partie',
    tipEn: 'Tip: Use your customer number to quickly identify the other party',
    color: 'from-blue-500 to-indigo-500'
  },
  {
    id: 'confirm',
    icon: Users,
    titleFr: '2. Les deux parties confirment',
    titleEn: '2. Both parties confirm',
    descFr: 'L\'acheteur et le vendeur reçoivent une notification et doivent tous deux confirmer les détails du transfert pour continuer.',
    descEn: 'Both buyer and seller receive a notification and must confirm the transfer details to proceed.',
    tipFr: 'Important: Les deux confirmations sont obligatoires pour sécuriser le transfert',
    tipEn: 'Important: Both confirmations are required to secure the transfer',
    color: 'from-purple-500 to-pink-500'
  },
  {
    id: 'payment',
    icon: CreditCard,
    titleFr: '3. L\'acheteur effectue le paiement',
    titleEn: '3. Buyer makes payment',
    descFr: 'Une fois confirmé, l\'acheteur envoie le paiement par Virement Interac. Middleman le conserve en sécurité jusqu\'à la vérification des billets.',
    descEn: 'Once confirmed, the buyer sends payment via Interac e-Transfer. Middleman holds it securely until tickets are verified.',
    tipFr: 'Frais: 6,95$ (≤125$), 9,95$ (125-250$) ou 19,95$ (>250$) selon le montant total du transfert.',
    tipEn: 'Fees: $6.95 (≤$125), $9.95 ($125-$250) or $19.95 (>$250) based on total transfer amount.',
    color: 'from-green-500 to-emerald-500'
  },
  {
    id: 'tickets',
    icon: Ticket,
    titleFr: '4. Le vendeur transfère les billets',
    titleEn: '4. Seller transfers tickets',
    descFr: 'Le vendeur transfère les billets via sa plateforme de billetterie à l\'adresse de vérification Middleman. Nos experts vérifient leur authenticité.',
    descEn: 'The seller transfers tickets via their ticketing platform to Middleman\'s verification address. Our experts verify their authenticity.',
    tipFr: 'Vérification: Notre équipe s\'assure que les billets sont valides et conformes',
    tipEn: 'Verification: Our team ensures tickets are valid and compliant',
    color: 'from-orange-500 to-amber-500'
  },
  {
    id: 'complete',
    icon: CheckCircle,
    titleFr: '5. Transfert complété!',
    titleEn: '5. Transfer complete!',
    descFr: 'Une fois vérifiés, les billets sont envoyés à l\'acheteur et le paiement est libéré au vendeur. Transaction sécurisée!',
    descEn: 'Once verified, tickets are sent to the buyer and payment is released to the seller. Secure transaction!',
    tipFr: 'Protection: Vous êtes protégé contre la fraude du début à la fin',
    tipEn: 'Protection: You\'re protected against fraud from start to finish',
    color: 'from-brand-500 to-green-500'
  }
];

const OnboardingTutorial = ({ onComplete, onSkip }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const { language } = useLanguage();
  
  const step = TUTORIAL_STEPS[currentStep];
  const isLastStep = currentStep === TUTORIAL_STEPS.length - 1;
  const isFirstStep = currentStep === 0;
  
  const title = language === 'fr' ? step.titleFr : step.titleEn;
  const desc = language === 'fr' ? step.descFr : step.descEn;
  const tip = language === 'fr' ? step.tipFr : step.tipEn;
  
  const nextStep = () => {
    if (isLastStep) {
      onComplete();
    } else {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(prev => prev + 1);
        setIsAnimating(false);
      }, 200);
    }
  };
  
  const prevStep = () => {
    if (!isFirstStep) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(prev => prev - 1);
        setIsAnimating(false);
      }, 200);
    }
  };
  
  const Icon = step.icon;
  
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Close button */}
        <button
          onClick={onSkip}
          className="absolute top-4 right-4 z-10 p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
          aria-label="Fermer"
        >
          <X className="h-5 w-5 text-gray-600" />
        </button>
        
        {/* Gradient header */}
        <div className={`bg-gradient-to-r ${step.color} p-8 text-white`}>
          <div className={`transition-all duration-200 ${isAnimating ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}>
            <div className="flex items-center justify-center mb-4">
              <div className="p-4 bg-white/20 rounded-2xl backdrop-blur-sm">
                <Icon className="h-12 w-12" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-center">{title}</h2>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6">
          <div className={`transition-all duration-200 ${isAnimating ? 'opacity-0 translate-x-4' : 'opacity-100 translate-x-0'}`}>
            <p className="text-gray-700 text-center text-lg mb-4">
              {desc}
            </p>
            
            {/* Tip box */}
            <div className="bg-brand-50 border border-brand-200 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <Shield className="h-5 w-5 text-brand-600 flex-shrink-0 mt-0.5" />
                <p className="text-brand-800 text-sm font-medium">{tip}</p>
              </div>
            </div>
          </div>
          
          {/* Progress indicators */}
          <div className="flex justify-center gap-2 mb-6">
            {TUTORIAL_STEPS.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setIsAnimating(true);
                  setTimeout(() => {
                    setCurrentStep(index);
                    setIsAnimating(false);
                  }, 200);
                }}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === currentStep 
                    ? 'w-8 bg-brand-600' 
                    : index < currentStep 
                      ? 'w-2 bg-brand-400' 
                      : 'w-2 bg-gray-300'
                }`}
              />
            ))}
          </div>
          
          {/* Navigation buttons */}
          <div className="flex justify-between items-center">
            <Button
              variant="ghost"
              onClick={prevStep}
              disabled={isFirstStep}
              className={`${isFirstStep ? 'opacity-0 pointer-events-none' : ''}`}
            >
              <ChevronLeft className="h-5 w-5 mr-1" />
              {language === 'fr' ? 'Précédent' : 'Previous'}
            </Button>
            
            <Button
              onClick={nextStep}
              className={`bg-gradient-to-r ${step.color} hover:opacity-90 text-white px-6`}
            >
              {isLastStep ? (
                <>
                  {language === 'fr' ? 'Commencer' : 'Get Started'}
                  <Sparkles className="h-5 w-5 ml-2" />
                </>
              ) : (
                <>
                  {language === 'fr' ? 'Suivant' : 'Next'}
                  <ChevronRight className="h-5 w-5 ml-1" />
                </>
              )}
            </Button>
          </div>
          
          {/* Skip link */}
          <button
            onClick={onSkip}
            className="w-full mt-4 text-gray-500 text-sm hover:text-gray-700 transition-colors"
          >
            {language === 'fr' ? 'Passer le tutoriel' : 'Skip tutorial'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Hook to manage tutorial state
export const useTutorial = () => {
  const [showTutorial, setShowTutorial] = useState(false);
  
  useEffect(() => {
    // Check if user has seen the tutorial
    const hasSeenTutorial = localStorage.getItem('middleman_tutorial_completed');
    if (!hasSeenTutorial) {
      // Small delay to let the page load first
      const timer = setTimeout(() => {
        setShowTutorial(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);
  
  const completeTutorial = () => {
    localStorage.setItem('middleman_tutorial_completed', 'true');
    setShowTutorial(false);
  };
  
  const skipTutorial = () => {
    localStorage.setItem('middleman_tutorial_completed', 'true');
    setShowTutorial(false);
  };
  
  const resetTutorial = () => {
    localStorage.removeItem('middleman_tutorial_completed');
    setShowTutorial(true);
  };
  
  return {
    showTutorial,
    completeTutorial,
    skipTutorial,
    resetTutorial
  };
};

export default OnboardingTutorial;
